import {
    DsIconModule,
    DsLoadingAreaConfiguration,
    DsLoadingAreaService,
    DsModalModule, DsProgressCircleModule,
    DsRowData,
    DsTableModule,
    DsTabsModule,
    DsToastModel,
    DsToastService,
} from '@bmw-ds/components';
import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CriteriaResponse } from '../../models/criteria.model';
import { CriteriaService } from '../../services/criteria.service';
import { CriteriaStoreService } from '../../services/criteria-store.service';
import { BreadcrumbComponent } from '../../components/breadcrumb/breadcrumb.component';
import { CosyUrlResponse } from '../../models/cosy-response.model';
import { StockService } from '../../services/stock.service';
import { DemoStockRequest } from '../../models/demo-stock-request.model';
import {
  DemoStockItem,
  DemoStockResponse,
} from '../../models/demo-stock-response.model';
import { ReplenishmentService } from '../../services/replenishment.service';
import { RequestVehicleRequest } from '../../models/request-vehicle.model';
import { AuthService } from '../../services/auth.service';
import { take } from 'rxjs';
import {COMMON_IMPORTS} from "../../shared/common-imports";

@Component({
  selector: 'app-demo-open-requests',
    imports: [
        DsTableModule,
        CommonModule,
        BreadcrumbComponent,
        DsTabsModule,
        DsIconModule,
        DsModalModule,
        DsProgressCircleModule,
        COMMON_IMPORTS
    ],
  templateUrl: './demo-open-requests.component.html',
  styleUrl: './demo-open-requests.component.scss',
  standalone: true,
})
export class DemoOpenRequestsComponent implements OnInit {
  criteriaData!: CriteriaResponse;
  rowsData: DsRowData[] = [];
  loadingAreaConfig: DsLoadingAreaConfiguration = { id: 'bodyLoading' };
  cancelReplenishment: RequestVehicleRequest = {
    vin: '',
    dealerCode: '',
    plantCode: '',
    storageLocation: '',
    purpose: '',
  };
  errorMsg = '';
  cancelRequestModalData = {
    vin: '',
  };
  isCancelRequestModal: boolean = false;
  isOpenDemoTabClicked: boolean = false;
  isCancelledDemoTabClicked: boolean = false;
  private readonly loadingAreaService = inject(DsLoadingAreaService);
  private readonly criteriaService = inject(CriteriaService);
  private readonly stockService = inject(StockService);
  private readonly replenishmentService = inject(ReplenishmentService);
  private readonly criteriaStoreService = inject(CriteriaStoreService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly toastService = inject(DsToastService);
  private readonly authService = inject(AuthService);

  addToast(tone: 'critical' | 'positive' | 'neutral', teastText: string): void {
    this.toastService.pushToast({
      id: 'error-toast',
      tone: tone,
      toastText: teastText,
    } as DsToastModel);
  }

  ngOnInit(): void {
    this.authService.isAuthenticated().pipe(take(1)).subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        this.loadCriteriaData(localStorage.getItem('dealerCode') ?? '');
      } else {
        window.location.href = this.authService.getAuthUrl();
      }
    });
  }

  loadCriteriaData(dealerCode: string): void {
    this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
    this.criteriaService.postCriteriaSelection(dealerCode).subscribe({
      next: (data: CriteriaResponse) => {
        this.criteriaData = data;
        localStorage.setItem('plantCode', this.criteriaData.plant);
        this.cdr.detectChanges();
        this.criteriaStoreService.setCriteriaData(data);
        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
        this.isOpenDemoTabClicked = true;
        setTimeout(() => {
          this.onTabClick('Open');
        });
      },
      error: (error: unknown) => {
        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
        console.error('Error fetching criteria data', error);
      },
    });
  }

  onTabClick(status: 'Open' | 'Cancelled'): void {
    console.log('CLICKED', status);
    this.loadingAreaConfig = {
      id: 'bodyLoading',
      loadingTitle: 'Loading',
      loadingText: status + ' Demo Requests',
    };
    this.errorMsg = '';
    this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
    const request: DemoStockRequest = {
      dealerCode: localStorage.getItem('dealerCode') || '',
      indicator: status === 'Open' ? 'R' : 'D',
    };
    console.log('request', request);
    this.rowsData = [];
    this.stockService.getDemoStock(request).subscribe({
      next: (response: DemoStockResponse) => {
        console.log('response', response);
        this.rowsData = (response.demoStockItemList || []).map(
          (item: DemoStockItem) => ({
            image: '',
            VIN: item.vin,
            modelCode: item.model,
            description: item.modelDescription,
            dateRequested: item.dateCreated,
            purpose: item.purpose,
          })
        );
        this.cdr.detectChanges();
        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
        this.rowsData.forEach((row, idx) => {
          const cosyRequest = { VIN: row['VIN'] };
          this.stockService
            .getTableCosyUrl(cosyRequest)
            .subscribe((response: CosyUrlResponse) => {
              this.rowsData[idx]['image'] = response.exturl;
            });
        });
      },
      error: (err) => {
        this.rowsData = [];
        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
        this.errorMsg = 'Error loading stock requests';
        console.error('Error loading stock requests', err);
      },
    });
  }

  onOpenTabClick(): void {
    this.isOpenDemoTabClicked = true;
    this.isCancelledDemoTabClicked = false;
    this.onTabClick('Open');
  }

  onCancelledTabClick(): void {
    this.isCancelledDemoTabClicked = true;
    this.isOpenDemoTabClicked = false;
    this.onTabClick('Cancelled');
  }

  openCancelRequestConfirmationModal(vin: string | number) {
    this.cancelRequestModalData.vin = vin.toString();
    this.isCancelRequestModal = true;
  }

  close() {
    this.isCancelRequestModal = false;
  }

  confirmCancelRequest() {
    this.loadingAreaConfig = {
      id: 'bodyLoading',
      loadingTitle: 'Loading...',
      loadingText: 'Cancelling Request',
    };
    this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
    this.cancelReplenishment.vin = this.cancelRequestModalData.vin;
    this.cancelReplenishment.dealerCode = '';
    this.cancelReplenishment.purpose = '';
    this.cancelReplenishment.plantCode = '';
    this.cancelReplenishment.storageLocation = '';

    this.replenishmentService
      .cancelReplenishment(this.cancelReplenishment)
      .subscribe({
        next: (response) => {
          console.log('Vehicle cancel successful:', response);
          this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
          this.addToast('positive', response.messageText);
          this.isCancelRequestModal = false;
        },
        error: (error) => {
          console.error('Error cancelling vehicle:', error);
          // this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
          this.addToast('critical', 'Demo Request Failed');
          this.isCancelRequestModal = false;
        },
      });
  }
}
